import { Doc } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import {
  equityTreatmentAcquisitionInvestorsApplyStepUpAtom,
  equityTreatmentAcquisitionInvestorsAtom,
  equityTreatmentSearchPhaseApplyStepUpAtom,
  equityTreatmentSearchPhaseAtom,
  equityTreatmentSearchersCoInvestApplyStepUpAtom,
  equityTreatmentSellerEquityRolloverApplyStepUpAtom,
  equityTreatmentSellerEquityRolloverAtom,
  includeInvestorPayoutInHistoricalAtom,
  inputEquityPromoteSearchersCarryPercentAtom,
  inputEquityTreatmentSearchersCoInvestAtom,
  inputIsEquityCalculationMethodUsingProjectCostAtom,
  isEquityInvestorsIncludedAtom,
  isExternalEquityAtom,
  isInvestorFundedSearchPhaseAtom,
  isSellerRollingOverEquityAtom,
} from "@/financeModels/owasco/general";
import { useIsModelReadOnly } from "@/hooks/useFinancialDeckPermissions";
import { numberToPercentInput, percentInputToNumber } from "@/utils/format";
import { Divider, Group, SegmentedControl, Stack, Text } from "@mantine/core";
import { useAtom, useAtomValue } from "jotai";
import { ChangeEvent } from "react";
import { TbLayoutBoardSplit } from "react-icons/tb";
import { InputPercent } from "../../../Sidebar/InputPercent";
import { InputToggle } from "../../../Sidebar/InputToggle";
import { EquityTreatmentToggle } from "./EquityTreatmentToggle";
import { OwnershipTable } from "./OwnershipTable";

const Panel = () => {
  const [isInvestorIncluded] = useAtom(isEquityInvestorsIncludedAtom);
  const [isSellerRollingOverEquity] = useAtom(isSellerRollingOverEquityAtom);
  const [investorsFundedSearchPhase] = useAtom(isInvestorFundedSearchPhaseAtom);
  const [
    equityCalculationMathodUsingProjectCost,
    setEquityCalculationMathodUsingProjectCost,
  ] = useAtom(inputIsEquityCalculationMethodUsingProjectCostAtom);
  const [
    includeInvestorPayoutInHistorical,
    setIncludeInvestorPayoutInHistorical,
  ] = useAtom(includeInvestorPayoutInHistoricalAtom);

  const [isModelShared] = useIsModelReadOnly();

  const [
    equityPromoteSearchersCarryPercent,
    setEquityPromoteSearchersCarryPercent,
  ] = useAtom(inputEquityPromoteSearchersCarryPercentAtom);

  const isExternalEquity = useAtomValue(isExternalEquityAtom);

  if (!isExternalEquity) {
    return (
      <Stack gap={4}>
        <Text size="sm" c="dimmed">
          This section will activate once you’ve added external equity to your
          capital structure — toggle on either ‘Seller rolling over equity?’ or
          ‘Do you have equity investors?’ above.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap={4}>
      <SegmentedControl
        disabled={isModelShared}
        fullWidth
        w={"100%"}
        data={[
          { label: "Equity Method", value: "equity" },
          { label: "Project Cost Method", value: "projectCost" },
        ]}
        value={
          equityCalculationMathodUsingProjectCost ? "projectCost" : "equity"
        }
        onChange={(value) => {
          setEquityCalculationMathodUsingProjectCost(
            value === "projectCost" ? true : false,
          );
        }}
        color="accent.6"
      />

      <Group my={5}>
        <LabelExplainer
          label={
            <Text size="xs">
              Difference between Project Cost vs. Equity Method
            </Text>
          }
          showIcon={true}
          explanation={
            <Doc path="@/docs/pages/model/sidebar/equityInvestorTermsDiffEquityVsProjectCost.mdx" />
          }
        />
      </Group>

      {!equityCalculationMathodUsingProjectCost && (
        <InputPercent
          mt={"sm"}
          label={
            <LabelExplainer
              label="Searcher Promote"
              explanation={
                <Doc path="@/docs/pages/model/sidebar/equityInvestorTermsPromoteSearchersCarry.mdx" />
              }
            />
          }
          value={numberToPercentInput(equityPromoteSearchersCarryPercent)}
          onChange={(value) =>
            setEquityPromoteSearchersCarryPercent(percentInputToNumber(value))
          }
        />
      )}

      <Divider my="sm" />

      <Text size="sm" fw={"bold"} mb="sm">
        Equity Treatment
      </Text>

      <EquityTreatmentToggle
        label="Search Phase Capital"
        equityModeAtom={equityTreatmentSearchPhaseAtom}
        applyStepUpAtom={equityTreatmentSearchPhaseApplyStepUpAtom}
        docPath="@/docs/pages/model/sidebar/equityTreatmentSearchPhase.mdx"
        disabled
        visible={investorsFundedSearchPhase}
      />

      <EquityTreatmentToggle
        label="Acquisition Investors"
        equityModeAtom={equityTreatmentAcquisitionInvestorsAtom}
        applyStepUpAtom={equityTreatmentAcquisitionInvestorsApplyStepUpAtom}
        docPath="@/docs/pages/model/sidebar/equityTreatmentAcquisitionInvestors.mdx"
        disabled
        visible={isInvestorIncluded}
      />

      <EquityTreatmentToggle
        label="Searcher Co-Invest"
        equityModeAtom={inputEquityTreatmentSearchersCoInvestAtom}
        applyStepUpAtom={equityTreatmentSearchersCoInvestApplyStepUpAtom}
        docPath="@/docs/pages/model/sidebar/equityTreatmentSearchersCoInvest.mdx"
      />

      <EquityTreatmentToggle
        label="Seller Equity Rollover"
        equityModeAtom={equityTreatmentSellerEquityRolloverAtom}
        applyStepUpAtom={equityTreatmentSellerEquityRolloverApplyStepUpAtom}
        docPath="@/docs/pages/model/sidebar/equityTreatmentSellerEquityRollover.mdx"
        visible={isSellerRollingOverEquity}
      />

      <OwnershipTable />

      <Divider my="sm" />

      <Text size="sm" fw={"bold"} mb="sm">
        Historical Adjustments
      </Text>

      <InputToggle
        labelWidth={240}
        label={
          <LabelExplainer
            label="Investor Payouts in Historicals?"
            explanation={
              <Doc path="@/docs/pages/model/sidebar/equityTreatmentIncludeInvestorPayoutInHistorical.mdx" />
            }
          />
        }
        checked={includeInvestorPayoutInHistorical}
        onChange={(event) => {
          setIncludeInvestorPayoutInHistorical(
            (event as ChangeEvent<HTMLInputElement>).currentTarget.checked,
          );
        }}
      />
    </Stack>
  );
};

export const ModuleEquityInvestorTerms = {
  key: "Equity Terms",
  icon: <TbLayoutBoardSplit />,
  control: "Equity Terms",
  panel: <Panel />,
};
